import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ArrowLeft } from 'lucide-react';
import { CoRegulateLogo } from './Logo';
import { useAuth } from '../contexts/AuthContext';

/**
 * Reusable public header component with mobile hamburger menu
 * Used across Landing, Paywall, Checkout, and PaymentSuccess pages
 */
const PublicHeader = ({ 
    currentPage = '',     // 'pricing', 'checkout', 'success' - for highlighting active nav
    showBackButton = false,
    backButtonText = 'Back',
    backButtonHref = '/pricing',
    rightContent = null   // Optional custom right-side content (e.g., CurrencySelector)
}) => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Logo links to dashboard if authenticated, otherwise landing page
    const logoHref = isAuthenticated ? '/home' : '/';

    const navLinks = [
        { href: '/how-it-works', label: 'How It Works', id: 'how-it-works' },
        { href: '/tools', label: 'Tools', id: 'tools' },
        { href: '/about', label: 'About', id: 'about' },
        { href: '/pricing', label: 'Pricing', id: 'pricing' },
    ];

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="public-header">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                <a href={logoHref} className="block" data-testid="header-logo">
                    <CoRegulateLogo size="md" />
                </a>
                
                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center gap-3">
                    {navLinks.map((link) => (
                        <a 
                            key={link.id}
                            href={link.href}
                            className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${
                                currentPage === link.id 
                                    ? 'text-[#1D6B6B] font-semibold'
                                    : 'text-[#7D8E95] hover:text-[#1D6B6B]'
                            }`}
                            data-testid={`nav-${link.id}-link`}
                        >
                            {link.label}
                        </a>
                    ))}
                    
                    {/* Custom right content (e.g., CurrencySelector) */}
                    {rightContent}
                    
                    {isAuthenticated ? (
                        <a 
                            href="/home"
                            className="inline-flex items-center justify-center px-4 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-medium rounded-full bg-[#1D6B6B] text-white hover:bg-[#145555] transition-all shadow-md"
                            data-testid="nav-dashboard-link"
                        >
                            Dashboard
                        </a>
                    ) : (
                        <>
                            <a 
                                href="/auth?mode=login"
                                className="flex items-center px-3 py-2 text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                                data-testid="nav-sign-in-link"
                            >
                                Sign in
                            </a>
                            <a 
                                href="/auth"
                                className="inline-flex items-center justify-center px-4 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-medium rounded-full bg-[#1D6B6B] text-white hover:bg-[#145555] transition-all shadow-md"
                                data-testid="nav-get-started-link"
                            >
                                Get Started
                            </a>
                        </>
                    )}
                    
                    {showBackButton && (
                        <button 
                            onClick={() => navigate(backButtonHref)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full bg-[#1D6B6B]/10 text-[#1D6B6B] hover:bg-[#1D6B6B]/20 transition-colors"
                            data-testid="back-button"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">{backButtonText}</span>
                            <span className="sm:hidden">Back</span>
                        </button>
                    )}
                </div>
                
                {/* Mobile Navigation */}
                <div className="flex md:hidden items-center gap-1">
                    {/* Custom right content on mobile (before menu) */}
                    {rightContent}
                    
                    {showBackButton ? (
                        <button
                            onClick={() => navigate(backButtonHref)}
                            className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full border-2 border-[#1D6B6B] text-[#1D6B6B] hover:bg-[#1D6B6B]/10 transition-all whitespace-nowrap"
                            data-testid="nav-mobile-back"
                        >
                            <ArrowLeft className="w-3 h-3 mr-0.5" />
                            Back
                        </button>
                    ) : (
                        <a 
                            href="/auth?mode=login"
                            className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full border-2 border-[#1D6B6B] text-[#1D6B6B] hover:bg-[#1D6B6B]/10 transition-all whitespace-nowrap"
                            data-testid="nav-mobile-sign-in"
                        >
                            Login
                        </a>
                    )}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="p-1.5 rounded-lg text-[#1D6B6B] hover:bg-[#1D6B6B]/10 transition-colors"
                        aria-label="Toggle menu"
                        data-testid="nav-mobile-menu-toggle"
                    >
                        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            
            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-[#E8E1D6]">
                    <div className="px-4 py-3 space-y-1">
                        {navLinks.map((link) => (
                            <a 
                                key={link.id}
                                href={link.href}
                                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                                    currentPage === link.id
                                        ? 'bg-[#1D6B6B]/10 text-[#1D6B6B]'
                                        : 'text-[#3D5A5A] hover:bg-[#FEF6EF]'
                                }`}
                                onClick={() => setMobileMenuOpen(false)}
                                data-testid={`nav-mobile-${link.id}`}
                            >
                                {link.label}
                            </a>
                        ))}
                        <div className="pt-3 border-t border-[#E8E1D6] mt-3">
                            {isAuthenticated ? (
                                <a 
                                    href="/home"
                                    className="block w-full text-center px-4 py-3 rounded-full bg-[#1D6B6B] text-white font-medium hover:bg-[#145555] transition-all"
                                    onClick={() => setMobileMenuOpen(false)}
                                    data-testid="nav-mobile-dashboard"
                                >
                                    Go to Dashboard
                                </a>
                            ) : (
                                <a 
                                    href="/auth"
                                    className="block w-full text-center px-4 py-3 rounded-full bg-[#1D6B6B] text-white font-medium hover:bg-[#145555] transition-all"
                                    onClick={() => setMobileMenuOpen(false)}
                                    data-testid="nav-mobile-get-started"
                                >
                                    Get Started Free
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default PublicHeader;
